if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.onGalleryArrowClick = this.onGalleryArrowClick.bind(this);
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.bindGalleryArrows();
        this.mql.addEventListener('change', () => this.bindGalleryArrows());
      }

      isDesktopThumbnailLayout() {
        return this.mql.matches && this.dataset.desktopLayout?.includes('thumbnail');
      }

      bindGalleryArrows() {
        if (!this.elements.viewer) return;
        const buttons = this.elements.viewer.querySelectorAll('.slider-button');
        buttons.forEach((button) => {
          button.removeEventListener('click', this.onGalleryArrowClick, true);
        });
        // Capture phase so we beat SliderComponent's scroll handler on desktop.
        buttons.forEach((button) => {
          button.addEventListener('click', this.onGalleryArrowClick, true);
        });
        this.syncDesktopArrowState();
      }

      onGalleryArrowClick(event) {
        if (!this.isDesktopThumbnailLayout()) return;

        // Desktop thumbnail layouts hide inactive slides — do NOT let the
        // default slider scroll (it blanks the main image / jumps to thumbs).
        event.preventDefault();
        event.stopImmediatePropagation();

        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        this.cycleDesktopMedia(direction);
      }

      cycleDesktopMedia(direction) {
        const items = Array.from(
          this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')
        );
        if (items.length < 2) return;
        const activeIndex = items.findIndex((el) => el.classList.contains('is-active'));
        const start = activeIndex < 0 ? 0 : activeIndex;
        const nextIndex = (start + direction + items.length) % items.length;
        const next = items[nextIndex];
        if (next?.dataset?.mediaId) this.setActiveMedia(next.dataset.mediaId, false);
      }

      syncDesktopArrowState() {
        if (!this.elements.viewer) return;
        const prev = this.elements.viewer.querySelector('button[name="previous"]');
        const next = this.elements.viewer.querySelector('button[name="next"]');
        if (!prev || !next) return;

        if (!this.isDesktopThumbnailLayout()) return;

        const items = Array.from(
          this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')
        );
        if (items.length < 2) {
          prev.setAttribute('disabled', 'disabled');
          next.setAttribute('disabled', 'disabled');
          return;
        }

        // Looping gallery — keep both arrows enabled.
        prev.removeAttribute('disabled');
        next.removeAttribute('disabled');
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          const mediaList = activeMedia.parentElement;

          // Desktop thumbnail layouts: keep the active image pinned — never
          // horizontal-scroll the list (that caused the blank main image).
          if (this.isDesktopThumbnailLayout()) {
            if (mediaList) mediaList.scrollLeft = 0;
            this.syncDesktopArrowState();
            return;
          }

          if (!this.mql.matches || this.elements.thumbnails) {
            mediaList.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        this.syncDesktopArrowState();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
